import React, { FC, useState } from "react";

import { Box, Collapse } from "@material-ui/core";
import SvgIcon from "@material-ui/core/SvgIcon";

import { ReactComponent as ChevronDownSvg } from "@remar/shared/dist/assets/icons/icon-chevron-down.svg";
import { ReactComponent as FolderSvg } from "@remar/shared/dist/assets/icons/icon-folder.svg";
import { ReactComponent as LocationSvg } from "@remar/shared/dist/assets/icons/icon-location.svg";
import { ReactComponent as MediaSvg } from "@remar/shared/dist/assets/icons/icon-media.svg";
import { ReactComponent as QuestionSvg } from "@remar/shared/dist/assets/icons/icon-question-bank.svg";
import { ReactComponent as UserSvg } from "@remar/shared/dist/assets/icons/icon-user-friend.svg";
import cn from "classnames";
import { useHistory } from "react-router-dom";

import { HomeSvg, SettingsSvg } from "assets/icons";

import { ReactComponent as FileSvg } from "assets/icons/icon-file.svg";

import Logo from "assets/images/logo.svg";

import { routes } from "core/constants";

import { Container, LogoContainer, NavItem, NavItemContainer } from "./styles";

const navItemList: (SideNavItem | SideNavItemNested)[] = [
	{
		id: "dashboard",
		title: "Dashboard",
		route: "/",
		icon: HomeSvg
	},
	{
		id: routes.course.getElementKey(),
		title: "Courses",
		route: routes.course.getPath(),
		icon: FolderSvg
	},
	{
		id: routes.questionbank.getElementKey(),
		title: "Question Bank",
		icon: QuestionSvg,
		subItems: [
			{
				id: routes.questionbank.getElementKey(),
				route: routes.questionbank.getPath(),
				title: "Questions"
			},
			{
				id: routes.communityPool.getElementKey(),
				route: routes.communityPool.getPath(),
				title: "Community Pool"
			}
		]
	},
	{
		id: routes.manageStudents.getElementKey(),
		title: "Users",
		route: routes.manageStudents.getPath(),
		icon: UserSvg
	},
	// {
	// 	id: routes.reports.getElementKey(),
	// 	title: "Reports",
	// 	route: routes.reports.getPath(),
	// 	icon: ClipboardSvg
	// },
	// {
	// 	id: routes.mediaLibrary.getElementKey(),
	// 	title: "Media Library",
	// 	route: routes.mediaLibrary.getPath(),
	// 	icon: MediaSvg
	// },
	{
		id: routes.fileVault.getElementKey(),
		title: "File vault",
		route: routes.fileVault.getPath(),
		icon: MediaSvg
	},
	{
		id: routes.manageLocations.getElementKey(),
		title: "Manage Locations",
		route: routes.manageLocations.getPath(),
		icon: LocationSvg
	},
	{
		id: routes.settings.getElementKey(),
		title: "Settings",
		icon: SettingsSvg,
		subItems: [
			{
				id: routes.manageTutorial.getElementKey(),
				route: `${routes.manageTutorial.getPath()}?isIntro=true`,
				title: "Manage Tutorial"
			},
			{
				id: routes.manageSchools.getElementKey(),
				route: routes.manageSchools.getPath(),
				title: "Manage Schools"
			},
			{
				id: routes.catAvailability.getElementKey(),
				route: routes.catAvailability.getPath(),
				title: "CAT Availability"
			}
		]
	},
	{
		id: routes.reports.getElementKey(),
		title: "Reports",
		route: routes.reports.getPath(),
		icon: FileSvg
	}
];

interface SideNavProps {}

interface NestedSideNavItemProps {
	Icon?: FC;
	title: string;
	subItems: SideNavItem[];
}

interface SideNavItem {
	id: string;
	route: string;
	icon?: FC;
	title: string;
}
interface SideNavItemNested {
	id: string;
	icon: FC;
	title: string;
	subItems: SideNavItem[];
}

const NestedSideNavItem: FC<NestedSideNavItemProps> = ({ Icon, title, subItems }) => {
	const [expanded, setExpanded] = useState(false);
	const history = useHistory();

	return (
		<NavItem onClick={() => setExpanded(!expanded)} className="navItem">
			<Box display="flex" flexDirection="column" width="100%">
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					width="100%"
					className={cn("item-box", { highlight: expanded })}
				>
					<Box display="flex">
						{Icon && (
							<SvgIcon>
								<Icon />
							</SvgIcon>
						)}
						<div className="title">{title}</div>
					</Box>
					<SvgIcon style={{ transform: `rotate(${expanded ? "0deg" : "270deg"})` }} className="expand-chevron">
						<ChevronDownSvg />
					</SvgIcon>
				</Box>
				<Collapse in={expanded} unmountOnExit>
					{subItems.map(item => (
						<NavItem
							route
							onClick={() => history.push(item.route)}
							className="navItem"
							key={`${item.id}-nested-inner`}
							highlight={history.location.pathname === item.route}
						>
							<div className="item-box">
								<div className="title nested">{item.title}</div>
							</div>
						</NavItem>
					))}
				</Collapse>
			</Box>
		</NavItem>
	);
};

const SideNav: React.FC<SideNavProps> = () => {
	const history = useHistory();
	const { location } = history;

	return (
		<Container>
			<LogoContainer>
				<img alt="logo" src={Logo} />
			</LogoContainer>
			<NavItemContainer>
				{navItemList.map(item => {
					const Icon = item.icon;
					const single = item as SideNavItem;
					const nested = item as SideNavItemNested;
					// If item have one-level depth
					if (single.route) {
						const { id, route, title } = single;
						let highlight = false;
						if (route !== "/") {
							highlight = location.pathname.includes(route);
						} else if (location.pathname === "/" && route === location.pathname) {
							highlight = true;
						}
						return (
							<NavItem
								onClick={() => history.push(route)}
								className="navItem"
								key={`${id}-single`}
								route
								highlight={highlight}
							>
								<div className="item-box">
									{Icon && (
										<SvgIcon>
											<Icon />
										</SvgIcon>
									)}
									<div className="title">{title}</div>
								</div>
							</NavItem>
						);
					}

					// If item have child routes
					if ((item as SideNavItemNested).subItems) {
						const { id, subItems, title } = nested;
						return <NestedSideNavItem subItems={subItems} title={title} Icon={Icon} key={`${id}-nested`} />;
					}
				})}
			</NavItemContainer>
		</Container>
	);
};

export default SideNav;
